import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { supabase } from '@/lib/supabase';
import { Toast } from '@/components/common/Toast';
import { OTPInput } from '@/components/auth/OTPInput';
import { useSellerSignupData } from '@/hooks/useSellerSignupData';

export function SellerSignupPage() {
  const navigate = useNavigate();
  const { accountInfo, updateAccountInfo } = useSellerSignupData();
  
  // localStorage에서 복원되지 않는 보안 관련 state (비밀번호)
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  
  // localStorage에서 복원된 데이터로 초기화
  const [formData, setFormData] = useState({
    email: accountInfo.email || '',
    password: '',
    confirmPassword: '',
    phone: accountInfo.phone || '',
    name: accountInfo.name || ''
  });
  
  // OTP 인증 상태
  const [isEmailVerified, setIsEmailVerified] = useState(accountInfo.isCodeVerified || false);
  const [, setVerificationToken] = useState('');
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>();
  const [nextAllowedAt, setNextAllowedAt] = useState<string | undefined>();
  
  // Loading state removed as not needed
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  // OTP 발송
  const handleSendOTP = async () => {
    if (!formData.email) {
      setToast({ show: true, message: '이메일을 입력해주세요' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setToast({ show: true, message: '올바른 이메일 형식을 입력해주세요' });
      return;
    }

    if (!passwordData.password || passwordData.password.length < 8) {
      setToast({ show: true, message: '비밀번호를 8자리 이상 입력해주세요' });
      return;
    }

    setIsResending(true);
    setOtpError('');

    try {
      const { data, error } = await supabase.functions.invoke('send-otp', {
        body: { 
          email: formData.email,
          purpose: 'signup',
          name: formData.name
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setShowOTPInput(true);
        setRemainingAttempts(data.remainingAttempts);
        setToast({ show: true, message: data.message });
      } else {
        setOtpError(data?.error || '인증번호 발송에 실패했습니다.');
        setRemainingAttempts(data?.remainingAttempts);
        setNextAllowedAt(data?.nextAllowedAt);
      }
    } catch (error: any) {
      console.error('OTP 발송 오류:', error);
      setOtpError(error.message || '인증번호 발송 중 오류가 발생했습니다.');
    } finally {
      setIsResending(false);
    }
  };

  // OTP 검증
  const handleVerifyOTP = async (otp: string) => {
    setIsOTPLoading(true);
    setOtpError('');

    try {
      const { data, error } = await supabase.functions.invoke('verify-otp', {
        body: { 
          email: formData.email,
          otp,
          purpose: 'signup'
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setIsEmailVerified(true);
        setVerificationToken(data.verificationToken);
        setShowOTPInput(false);
        updateAccountInfo({ isCodeVerified: true });
        setToast({ show: true, message: '이메일 인증이 완료되었습니다!' });
      } else {
        setOtpError(data?.error || '인증번호가 올바르지 않습니다.');
        setRemainingAttempts(data?.remainingAttempts);
      }
    } catch (error: any) {
      console.error('OTP 검증 오류:', error);
      setOtpError(error.message || '인증번호 확인 중 오류가 발생했습니다.');
    } finally {
      setIsOTPLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 비밀번호는 localStorage에 저장하지 않음
    if (field === 'password' || field === 'confirmPassword') {
      setPasswordData(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      // 다른 필드는 localStorage에 저장
      updateAccountInfo({ [field]: value });
    }
  };



  const isFormValid = () => {
    return formData.email && 
           passwordData.password && 
           passwordData.confirmPassword && 
           formData.phone && 
           formData.name && 
           passwordData.password === passwordData.confirmPassword &&
           passwordData.password.length >= 8 &&
           isEmailVerified;
  };

  const handleNext = () => {
    if (!isFormValid()) {
      if (!isEmailVerified) {
        setToast({ show: true, message: '이메일 인증을 완료해주세요' });
      } else if (passwordData.password !== passwordData.confirmPassword) {
        setToast({ show: true, message: '비밀번호가 일치하지 않습니다' });
      } else if (passwordData.password.length < 8) {
        setToast({ show: true, message: '비밀번호는 8자리 이상이어야 합니다' });
      } else {
        setToast({ show: true, message: '모든 필드를 입력해주세요' });
      }
      return;
    }

    // 비밀번호를 sessionStorage에 임시 저장 (회원가입 완료 후 삭제됨)
    sessionStorage.setItem('temp_signup_password', passwordData.password);

    // 다음 단계로 이동 - 사업자 정보 입력
    // localStorage를 사용하므로 location.state 대신 직접 이동
    navigate('/signup/seller/business');
  };

  return (
    <div className="min-h-screen bg-white">
      <PageContainer>
        {/* 헤더 */}
        <div className="flex items-center justify-center relative mb-8">
          <button
            onClick={handleBack}
            className="absolute left-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <img
              src="/icons/icon_back.svg"
              alt="뒤로가기"
              className="w-6 h-6"
            />
          </button>
          <h1 className="text-2xl font-bold text-[#222222]">회원가입</h1>
        </div>

        {/* 단계 표시 */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-[#51C854] rounded-full flex items-center justify-center">
              <span className="text-white font-medium">1</span>
            </div>
            <div className="w-16 h-0.5 bg-[#DEDEDE] mx-2"></div>
            <div className="w-8 h-8 bg-[#DEDEDE] rounded-full flex items-center justify-center">
              <span className="text-[#999999] font-medium">2</span>
            </div>
            <div className="w-16 h-0.5 bg-[#DEDEDE] mx-2"></div>
            <div className="w-8 h-8 bg-[#DEDEDE] rounded-full flex items-center justify-center">
              <span className="text-[#999999] font-medium">3</span>
            </div>
          </div>
        </div>

        {/* 입력 폼 */}
        <form onSubmit={(e) => e.preventDefault()} name="seller-signup-form" className="max-w-[300px] mx-auto space-y-6">
          {/* 이메일 입력 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              이메일
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                name="email"
                id="email"
                autoComplete="username"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="이메일을 입력해주세요"
                className="flex-1 h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
              />
              {!isEmailVerified && (
                <button
                  type="button"
                  onClick={handleSendOTP}
                  disabled={isResending}
                  className={`px-3 h-[30px] font-medium text-xs rounded-lg transition-all whitespace-nowrap min-w-[60px] w-full sm:w-auto ${
                    !isResending
                      ? 'bg-[#4CD964] text-white hover:bg-[#45B85C] active:scale-95'
                      : 'bg-[#DEDEDE] text-[#222222] cursor-not-allowed'
                  }`}
                >
                  {isResending ? '발송중...' : '인증받기'}
                </button>
              )}
              
              {isEmailVerified && (
                <div className="px-3 h-[30px] flex items-center w-full sm:w-auto justify-center sm:justify-start">
                  <span className="text-[#4CD964] text-xs font-medium">✓ 인증완료</span>
                </div>
              )}
            </div>
          </div>

          {/* OTP 입력 */}
          {showOTPInput && !isEmailVerified && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
              <div className="text-sm text-gray-600 mb-3 text-center">
                <strong>{formData.email}</strong>로 발송된 인증번호를 입력해주세요
              </div>
              
              <OTPInput
                onComplete={handleVerifyOTP}
                onResend={handleSendOTP}
                isLoading={isOTPLoading}
                isResending={isResending}
                error={otpError}
                remainingAttempts={remainingAttempts}
                nextAllowedAt={nextAllowedAt}
              />
            </div>
          )}

          {/* 비밀번호 입력 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              비밀번호(8자리)
            </label>
            <input
              type="password"
              name="password"
              id="new-password"
              autoComplete="new-password"
              value={passwordData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              placeholder="비밀번호 8자리를 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* 비밀번호 재확인 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              비밀번호 재확인
            </label>
            <input
              type="password"
              name="confirm-password"
              id="confirm-password"
              autoComplete="new-password"
              value={passwordData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              placeholder="비밀번호를 다시 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* 전화번호 입력 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              전화번호(번호만 입력)
            </label>
            <input
              type="tel"
              name="phone"
              id="phone"
              autoComplete="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="예시) 01011111111"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* 이름 입력 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              이름
            </label>
            <input
              type="text"
              name="name"
              id="name"
              autoComplete="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="이름을 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>
        </form>

        {/* 다음 단계 버튼 */}
        <div className="max-w-[360px] mx-auto mt-12">
          <button
            onClick={handleNext}
            disabled={!isFormValid()}
            className={`w-full h-[31px] font-semibold text-xl rounded-xl transition-colors ${
              isFormValid() 
                ? 'bg-[#51C854] text-white hover:bg-[#46B649]' 
                : 'bg-[#DEDEDE] text-[#999999] cursor-not-allowed'
            }`}
          >
            다음 단계
          </button>
        </div>
      </PageContainer>

      {/* 토스트 메시지 */}
      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </div>
  );
}
